import { Box, CircularProgress, Typography } from "@mui/material"
import type { Row } from "@tanstack/react-table"
import type { FC } from "react"

import { useSalesOrderPreview } from "../../hooks/useSalesOrderPreview"

import { SalesOrderPreviewTable } from "./SalesOrderPreviewTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import type {
  CustomerLocationCombination,
  CustomerOrderDateRangeType,
  SalesOrderStatus,
} from "@/graphql/codegen/graphql"

interface CustomerOrderRowDetailsProps {
  row: Row<CustomerLocationCombination>
  query?: string
  statuses?: SalesOrderStatus[]
  dateFrom?: string
  dateTo?: string
  dateRangeType?: CustomerOrderDateRangeType
}

export const CustomerOrderRowDetails: FC<CustomerOrderRowDetailsProps> = ({
  row,
  query,
  statuses,
  dateFrom,
  dateTo,
  dateRangeType,
}) => {
  const { combinationId, company, shipToLocation } = row.original

  const { salesOrders, isFetching, errorMessage, sorting, onSortingChange } = useSalesOrderPreview({
    companyId: company.companyId,
    shipToLocationId: shipToLocation.locationId,
    query,
    statuses,
    dateFrom,
    dateTo,
    dateRangeType,
  })

  return (
    <Box
      className='flex flex-col gap-4 bg-primary-100 p-6'
      data-testid={`customer-orders.row-details.container-${combinationId}`}
    >
      {isFetching && !salesOrders.length && (
        <Box className='flex items-center justify-center p-8'>
          <CircularProgress />
        </Box>
      )}

      {!isFetching && errorMessage && (
        <Typography color='error' data-testid={`customer-orders.row-details.error-${combinationId}`}>
          {errorMessage}
        </Typography>
      )}

      {!isFetching && !errorMessage && !salesOrders.length && <InformationMissingAlert title='No sales orders found' />}

      {!isFetching && !errorMessage && salesOrders.length > 0 && (
        <SalesOrderPreviewTable data={salesOrders} sorting={sorting} onSortingChange={onSortingChange} />
      )}
    </Box>
  )
}
